import React, { useEffect } from "react";
import {
  Button,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";

// Formik validation
import * as Yup from "yup";
import { useFormik } from "formik";
import { withTranslation } from "react-i18next";
import { scrollToFirstFormikError } from "../../../helpers/utils";

const AddAuctionThemeModal = ({ auctionTheme, isModalOpen, onCancel, onSave, loader, t }) => {
  const validation = useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,
    validateOnChange: true,

    initialValues: {
      code_pt: auctionTheme?.code_pt || "",
      description_pt: auctionTheme?.description_pt || "",
      code_en: auctionTheme?.code_en || "",
      description_en: auctionTheme?.description_en || "",
    },
    validationSchema: Yup.object({
      code_pt: Yup.string()
        .max(10, t("validation.validataionMessageForMax10Chars"))
        .required(
          t("common.requiredValidationMsg", {
            field: `${t("common.codePt")}`,
          })
        ),
      code_en: Yup.string()
        .max(10, t("validation.validataionMessageForMax10Chars"))
        .required(
          t("common.requiredValidationMsg", {
            field: `${t("common.codeEn")}`,
          })
        ),
      description_pt: Yup.string().required(
        t("common.requiredValidationMsg", {
          field: `${t("common.descriptionPt")}`,
        })
      ),
      description_en: Yup.string().required(
        t("common.requiredValidationMsg", {
          field: `${t("common.descriptionEn")}`,
        })
      ),
    }),
    onSubmit: (values) => {
      onSave(values, validation);
    },
  });

  useEffect(() => {
    validation.resetForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen]);

  const handleModalClose = () => {
    if (!loader) {
      validation.resetForm();
      onCancel();
    }
  };

  return (
    <Modal centered isOpen={isModalOpen} toggle={handleModalClose} fade={false}>
      <ModalHeader>
        {`${
          auctionTheme?.id
            ? t("common.editTitle", {
                module: `${t("settings.auctions.auctionThemes.auctionTheme")} `,
              })
            : t("common.addTitle", {
                module: `${t("settings.auctions.auctionThemes.auctionTheme")} `,
              })
        } `}
      </ModalHeader>
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          validation.handleSubmit();
          return false;
        }}
        action="#"
      >
        <ModalBody>
          {/* form */}
          <div className="mb-3">
            <Label htmlFor="code" className="form-label">
              {t("common.code")}
            </Label>
            <Input
              name="code_pt"
              className="form-control"
              placeholder={`${t("common.enter")} ${t("settings.auctions.auctionThemes.auctionTheme")} ${t(
                "common.code"
              )}`}
              type="text"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.code_pt || ""}
              invalid={validation.touched.code_pt && validation.errors.code_pt ? true : false}
            />
            {validation.touched.code_pt && validation.errors.code_pt ? (
              <FormFeedback type="invalid">{validation.errors.code_pt}</FormFeedback>
            ) : null}
          </div>
          <div className="mb-3">
            <Label htmlFor="description" className="form-label">
              {t("common.description")}
            </Label>
            <Input
              name="description_pt"
              className="form-control"
              placeholder={`${t("common.enter")} ${t("settings.auctions.auctionThemes.auctionTheme")} ${t(
                "common.description"
              )}`}
              type="text"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.description_pt || ""}
              invalid={validation.touched.description_pt && validation.errors.description_pt ? true : false}
            />
            {validation.touched.description_pt && validation.errors.description_pt ? (
              <FormFeedback type="invalid">{validation.errors.description_pt}</FormFeedback>
            ) : null}
          </div>
          <div className="mb-3">
            <Label htmlFor="code english" className="form-label">
              {t("common.codeEn")}
            </Label>
            <Input
              name="code_en"
              className="form-control"
              placeholder={`${t("common.enter")} ${t("settings.auctions.auctionThemes.auctionTheme")} ${t(
                "common.codeEn"
              )}`}
              type="text"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.code_en || ""}
              invalid={validation.touched.code_en && validation.errors.code_en ? true : false}
            />
            {validation.touched.code_en && validation.errors.code_en ? (
              <FormFeedback type="invalid">{validation.errors.code_en}</FormFeedback>
            ) : null}
          </div>
          <div className="mb-3">
            <Label htmlFor="description" className="form-label">
              {t("common.descriptionEn")}
            </Label>
            <Input
              name="description_en"
              className="form-control"
              placeholder={`${t("common.enter")} ${t("settings.auctions.auctionThemes.auctionTheme")} ${t(
                "common.descriptionEn"
              )}`}
              type="text"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.description_en || ""}
              invalid={validation.touched.description_en && validation.errors.description_en ? true : false}
            />
            {validation.touched.description_en && validation.errors.description_en ? (
              <FormFeedback type="invalid">{validation.errors.description_en}</FormFeedback>
            ) : null}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="dark" onClick={handleModalClose} disabled={loader}>
            {t("common.cancel")}
          </Button>
          <Button
            color="primary"
            disabled={loader}
            className="btn-load d-flex"
            type="submit"
            onClick={() => scrollToFirstFormikError(validation.errors)}
          >
            {loader ? (
              <span className="d-flex align-items-center ">
                <Spinner size="xs" className="flex-shrink-0">
                  {t("common.loading")}...
                </Spinner>
              </span>
            ) : null}
            <span className={`flex-grow-1 ${loader ? "ms-2" : ""}`}>{t("common.save")}</span>
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default withTranslation()(AddAuctionThemeModal);
